import { Box, TextField } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import PermIdentityRoundedIcon from '@mui/icons-material/PermIdentityRounded'
import RowActions from './CRM-partial/RowActions'
import RowModal from './CRM-partial/RowModal'
import CRMRowModal from './CRM-partial/CRMRowModal'
import dayjs from 'dayjs'
import DueDateField from './CRM-partial/DueDateField'
import type {
  APICrmReference,
  APICrmTask,
  CrmTaskUpdateRequest,
} from '@/services/crmService'
import { formatCrmDate, getPriorityIcon } from '@/utils/crm'

/** A grid row: a listed task, or the modal's draft (partially filled). */
export type CrmGridRow = Partial<APICrmTask> & { id: number }

export type CrmRowChangeHandler = (
  row: CrmGridRow,
  patch: CrmTaskUpdateRequest
) => void

type CrmColumnsOptions = {
  reference?: APICrmReference
  /** The modal draft edits every field; list rows only change their status. */
  onRowChange?: CrmRowChangeHandler
  isDarkMode?: boolean
  /** Modal draft: meter number is an input and there is no actions column. */
  draft?: boolean
  /** Draft only: meter number is prefilled and cannot be changed. */
  meterLocked?: boolean
}

const cellTextSx = (isDarkMode: boolean) => ({
  color: isDarkMode ? '#FFFFFF' : '#003F61',
  fontFamily: 'Rubik',
  fontSize: '14px',
  fontStyle: 'normal',
})

const dateTextSx = (isDarkMode: boolean) => ({
  color: isDarkMode ? '#9CA3AF' : '#C0C2D6',
  fontFamily: 'Rubik',
  fontSize: '14px',
  fontStyle: 'normal',
})

/** Reference name for an id, falling back to the name the row carries. */
const nameOf = <T extends { id: number; name: string }>(
  list: T[] | undefined,
  id: number | null | undefined,
  fallback: string | null | undefined
) => list?.find(item => item.id === id)?.name ?? fallback ?? null

const COLUMN_MIN_WIDTH = 140

const columnsConfig = ({
  reference,
  onRowChange,
  isDarkMode = false,
  draft = false,
  meterLocked = false,
}: CrmColumnsOptions): GridColDef<CrmGridRow>[] => {
  const typeOptions = (reference?.types ?? []).map(t => ({
    id: t.id,
    label: t.name,
  }))
  const assigneeOptions = (reference?.assignees ?? []).map(a => ({
    id: a.id,
    label: a.name,
  }))
  const priorityOptions = (reference?.priorities ?? []).map(p => ({
    id: p.value,
    label: p.name,
  }))

  const readOnly = !draft
  const FLEX = { flex: 1, minWidth: COLUMN_MIN_WIDTH }

  const columns: GridColDef<CrmGridRow>[] = [
    {
      field: 'typeId',
      headerName: 'סוג המשימה',
      ...FLEX,
      sortable: false,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => (
        <RowModal
          label={nameOf(
            reference?.types,
            params.row.typeId,
            params.row.typeName
          )}
          options={typeOptions}
          disabled={readOnly}
          onChange={id => {
            if (Number(id) !== params.row.typeId)
              onRowChange?.(params.row, { typeId: Number(id) })
          }}
        />
      ),
    },
    {
      field: 'meterNumber',
      headerName: 'מספר מד',
      ...FLEX,
      sortable: false,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) =>
        draft ? (
          <TextField
            variant="standard"
            size="small"
            dir="rtl"
            placeholder="מספר מד"
            value={params.row.meterNumber ?? ''}
            disabled={meterLocked}
            onChange={e =>
              onRowChange?.(params.row, { meterNumber: e.target.value })
            }
            // DataGrid would otherwise swallow keys (space, arrows) for cell navigation.
            onKeyDown={e => e.stopPropagation()}
            InputProps={{ disableUnderline: meterLocked }}
            sx={{
              mt: '12px',
              '& .MuiInputBase-input': cellTextSx(isDarkMode),
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: cellTextSx(isDarkMode).color,
              },
            }}
          />
        ) : (
          <Box textAlign="right" sx={cellTextSx(isDarkMode)}>
            {params.row.meterNumber || '-'}
          </Box>
        ),
    },
    {
      field: 'assigneeId',
      headerName: 'משויך',
      ...FLEX,
      sortable: false,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => (
        <RowModal
          label={nameOf(
            reference?.assignees,
            params.row.assigneeId,
            params.row.assigneeName
          )}
          Icon={PermIdentityRoundedIcon}
          optionIcon={PermIdentityRoundedIcon}
          options={assigneeOptions}
          disabled={readOnly}
          onChange={id => {
            if (Number(id) !== params.row.assigneeId)
              onRowChange?.(params.row, { assigneeId: Number(id) })
          }}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'ת.יצירה',
      ...FLEX,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => (
        <Box textAlign="right" sx={dateTextSx(isDarkMode)}>
          {formatCrmDate(params.row.createdAt)}
        </Box>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'ת.עדכון אחרון',
      ...FLEX,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => (
        <Box textAlign="right" sx={dateTextSx(isDarkMode)}>
          {formatCrmDate(params.row.updatedAt)}
        </Box>
      ),
    },
    {
      field: 'dueAt',
      headerName: 'ת.יעד',
      ...FLEX,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => (
        <DueDateField
          // Remount when the value changes so the calendar opens on it.
          key={params.row.dueAt ?? 'none'}
          value={params.row.dueAt}
          overdue={draft ? undefined : params.row.overdue}
          disabled={readOnly}
          onChange={day => {
            if (day == null) onRowChange?.(params.row, { clearDueAt: true })
            // A due date is a whole day: send its local end so it is not overdue during that day.
            else
              onRowChange?.(params.row, {
                dueAt: dayjs(day).endOf('day').format(),
              })
          }}
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'עדיפות',
      ...FLEX,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => {
        const priority = params.row.priority
        const icon = getPriorityIcon(priority)
        const label =
          reference?.priorities.find(p => p.value === priority)?.name ??
          (priority != null ? String(priority) : null)
        return (
          <RowModal
            label={label}
            options={priorityOptions}
            disabled={readOnly}
            Icon={icon ? () => <img src={icon} alt="Priority" /> : null}
            onChange={id => {
              if (Number(id) !== priority)
                onRowChange?.(params.row, { priority: Number(id) })
            }}
          />
        )
      },
    },
    {
      field: 'statusId',
      headerName: 'סטטוס',
      ...FLEX,
      sortable: false,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => {
        const status = reference?.statuses.find(
          s => s.id === params.row.statusId
        )
        return (
          <CRMRowModal
            label={status?.name ?? params.row.statusName}
            section={status?.section ?? params.row.section}
            statuses={reference?.statuses ?? []}
            disabled={!onRowChange}
            onChange={statusId => {
              if (statusId !== params.row.statusId)
                onRowChange?.(params.row, { statusId })
            }}
          />
        )
      },
    },
    {
      field: 'updatedByName',
      headerName: 'עודכן ע״י',
      ...FLEX,
      ...FLEX,
      sortable: false,
      editable: false,
      renderCell: (params: GridRenderCellParams<CrmGridRow>) => (
        <Box display="flex" alignItems="center" gap={1}>
          <PermIdentityRoundedIcon
            sx={{ color: isDarkMode ? '#9CA3AF' : '#C0C2D6' }}
          />
          <Box sx={dateTextSx(isDarkMode)}>
            {params.row.updatedByName || '-'}
          </Box>
        </Box>
      ),
    },
  ]

  if (!draft) {
    columns.push({
      field: 'actions',
      type: 'actions',
      editable: false,
      resizable: false,
      getActions: params => [
        <RowActions key="row-actions" taskId={params.row.id} />,
      ],
    })
  }

  return columns
}
export default columnsConfig
